// SCR-FND-04 Settings and SCR-FND-05 user sheet (frozen P0 mockup). Owner only (the route and the database both refuse others).
// SET-02 policies one at a time, GEN-19 business language, GEN-20 users, GEN-06 audit log.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  Input,
  List,
  ListRow,
  Notice,
  Section,
  SheetActions,
  SkeletonList,
  Tabs,
} from '../../design/kit';
import { Refusal } from '../../data/auth';
import {
  createUser,
  loadAudit,
  loadSettings,
  loadUsers,
  setBusinessLanguage,
  setUserActive,
  updatePolicy,
  updateUserRole,
  type CreatedUser,
  type PolicyKey,
  type UserRow,
} from '../../data/settings';
import { applyLanguage, languages, rememberBusinessLanguage, type Language } from '../../i18n';
import { userLanguage } from '../../lib/ux/preferences';
import { Code, Name } from '../../i18n/Isolate';
import { messageKey } from '../../lib/errors';
import { formatDate, formatDateTime } from '../../lib/format';
import { roles, type Role } from '../../app/pages';
import { useSession } from '../../app/session';

const LANGUAGE_NAMES: Record<Language, string> = { ar: 'العربية', fr: 'Français', en: 'English' };
const POLICIES: { key: PolicyKey; label: string; icon: 'return' | 'clock' | 'alert' }[] = [
  { key: 'return_days', label: 'returnwin', icon: 'return' },
  { key: 'due_days', label: 'overdue', icon: 'clock' },
  { key: 'expiry_alert_days', label: 'expiry', icon: 'alert' },
];

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('');

const refusalCode = (err: unknown) => (err instanceof Refusal ? err.code : 'SERVER_ERROR');

type Sheet =
  | { kind: 'policy'; key: PolicyKey; value: number }
  | { kind: 'language' }
  | { kind: 'new-user' }
  | { kind: 'edit-user'; user: UserRow }
  | null;

// ADR-087: one family per tab; the open tab lives in the address (?tab=) so a refresh or a link returns to it.
type SettingsTab = 'biz' | 'policies' | 'users' | 'activity';
const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'biz', label: 'sec.biz' },
  { id: 'policies', label: 'sec.policies' },
  { id: 'users', label: 'sec.users' },
  { id: 'activity', label: 'tab.activity' },
];

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings', 'v_settings'], queryFn: loadSettings });
  const users = useQuery({ queryKey: ['settings', 'v_users'], queryFn: loadUsers });
  const audit = useQuery({ queryKey: ['settings', 'v_audit'], queryFn: loadAudit });
  const [sheet, setSheet] = useState<Sheet>(null);
  const { userId } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((x) => x.id === params.get('tab')) ? (params.get('tab') as SettingsTab) : 'biz';

  const refresh = () => void qc.invalidateQueries({ queryKey: ['settings'] });
  const close = () => setSheet(null);
  const s = settings.data;

  if (settings.isError) return <Notice tone="danger">{t(messageKey(refusalCode(settings.error)))}</Notice>;

  return (
    <>
      <Tabs
        label={t('tabs.settings')}
        value={tab}
        onChange={(id) =>
          setParams(
            (p) => {
              p.set('tab', id);
              return p;
            },
            { replace: true },
          )
        }
        items={TABS.map((x) => ({ id: x.id, label: t(x.label) }))}
      />
      {tab === 'biz' && (
        <div role="tabpanel" aria-label={t('sec.biz')}>
          <Section title={t('sec.biz')}>
            {!s ? (
              <SkeletonList rows={4} label={t('loading')} />
            ) : (
              <List label={t('sec.biz')}>
                <ListRow lead={{ icon: 'file' }} title={t('bizname')} sub={<Name>{s.business_name}</Name>} />
                <ListRow
                  lead={{ icon: 'info' }}
                  title={t('bizlang')}
                  value={LANGUAGE_NAMES[s.language]}
                  badge={
                    <Button pill variant="quiet" onClick={() => setSheet({ kind: 'language' })}>
                      {t('edit')}
                    </Button>
                  }
                />
                <ListRow
                  lead={{ icon: 'clock' }}
                  title={t('startdate')}
                  value={formatDate(s.business_start_date, lang)}
                />
                {/* One row each: combined, the time zone broke mid-word on phones (FND-D-07). */}
                <ListRow lead={{ icon: 'info' }} title={t('country')} value={t('algeria')} />
                <ListRow lead={{ icon: 'cash' }} title={t('currency')} value={t('dzd')} />
                <ListRow lead={{ icon: 'clock' }} title={t('tz')} value={<Code>{s.time_zone}</Code>} />
              </List>
            )}
          </Section>
        </div>
      )}
      {tab === 'policies' && (
        <div role="tabpanel" aria-label={t('sec.policies')}>
          <Section title={t('sec.policies')}>
            {!s ? (
              <SkeletonList rows={4} label={t('loading')} />
            ) : (
              <List label={t('sec.policies')}>
                {POLICIES.map((p) => (
                  <ListRow
                    key={p.key}
                    lead={{ icon: p.icon }}
                    title={t(p.label)}
                    value={t('days', { n: s[p.key] })}
                    badge={
                      <Button
                        pill
                        variant="quiet"
                        onClick={() => setSheet({ kind: 'policy', key: p.key, value: s[p.key] })}
                        data-testid={`edit-${p.key}`}
                      >
                        {t('edit')}
                      </Button>
                    }
                  />
                ))}
                <ListRow lead={{ icon: 'tag' }} title={t('discount')} value={t('nocap')} />
              </List>
            )}
          </Section>
        </div>
      )}
      {tab === 'users' && (
        <div role="tabpanel" aria-label={t('sec.users')}>
          <Section
            title={t('sec.users')}
            count={users.data?.length}
            extra={
              <Button pill variant="main" icon="plus" onClick={() => setSheet({ kind: 'new-user' })}>
                {t('newuser')}
              </Button>
            }
          >
            {!users.data ? (
              <SkeletonList label={t('loading')} />
            ) : users.data.length <= 1 ? (
              <>
                <UsersList rows={users.data} onEdit={(u) => setSheet({ kind: 'edit-user', user: u })} />
                <EmptyState icon="users" title={t('empty.title')} text={t('empty.text')} />
              </>
            ) : (
              <UsersList rows={users.data} onEdit={(u) => setSheet({ kind: 'edit-user', user: u })} />
            )}
          </Section>

          <Section title={t('sec.roles')}>
            <List label={t('sec.roles')}>
              {roles.map((r) => (
                <ListRow key={r} lead={{ icon: 'users' }} title={t(`role.${r}`)} sub={t(`role.${r}.d`)} />
              ))}
            </List>
          </Section>
        </div>
      )}
      {tab === 'activity' && (
        <div role="tabpanel" aria-label={t('tab.activity')}>
          <Section title={t('sec.audit')}>
            {!audit.data ? (
              <SkeletonList label={t('loading')} />
            ) : audit.data.length === 0 ? (
              <Notice tone="info">{t('audit.empty')}</Notice>
            ) : (
              <List label={t('sec.audit')}>
                {audit.data.map((a) => (
                  <ListRow
                    key={a.id}
                    lead={{ icon: 'clock' }}
                    title={i18n.exists(`audit.${a.action}`) ? t(`audit.${a.action}`) : a.action}
                    sub={
                      <>
                        {formatDateTime(a.at, lang)} · <Name>{a.actor_name ?? '—'}</Name>
                      </>
                    }
                  />
                ))}
              </List>
            )}
          </Section>

          <Section title={t('sec.about')}>
            <List label={t('sec.about')}>
              <ListRow lead={{ icon: 'info' }} title={t('version')} value={<Code>{__APP_VERSION__}</Code>} />
              <ListRow
                lead={{ icon: 'info' }}
                title={t('env')}
                value={import.meta.env.PROD ? t('env.production') : t('env.development')}
              />
            </List>
          </Section>
        </div>
      )}

      {sheet?.kind === 'policy' && (
        <PolicySheet
          sheet={sheet}
          onClose={close}
          onSaved={() => {
            close();
            refresh();
          }}
        />
      )}
      {sheet?.kind === 'language' && s && (
        <LanguageSheet
          current={s.language}
          onClose={close}
          onSaved={(l) => {
            close();
            rememberBusinessLanguage(l);
            // The owner's screen follows unless they chose their own language (ADR-110).
            if (!userLanguage(userId)) void applyLanguage(l);
            refresh();
          }}
        />
      )}
      {sheet?.kind === 'new-user' && <NewUserSheet onClose={close} onCreated={refresh} />}
      {sheet?.kind === 'edit-user' && (
        <EditUserSheet
          user={sheet.user}
          onClose={close}
          onSaved={() => {
            close();
            refresh();
          }}
        />
      )}
    </>
  );
}

function UsersList({ rows, onEdit }: { rows: UserRow[]; onEdit: (u: UserRow) => void }) {
  const { t } = useTranslation();
  return (
    <List label={t('sec.users')}>
      {rows.map((u) => (
        <ListRow
          key={u.user_id}
          testId={`user-${u.email}`}
          lead={{ initials: initials(u.display_name) }}
          title={<Name>{u.display_name}</Name>}
          sub={<Code>{u.email}</Code>}
          badge={
            <>
              <Badge tone="primary" icon={false}>
                {t(`role.${u.role}`)}
              </Badge>
              {u.active ? <Badge tone="success">{t('active')}</Badge> : <Badge>{t('inactive')}</Badge>}
            </>
          }
          actions={
            <Button pill variant="quiet" onClick={() => onEdit(u)}>
              {t('edit')}
            </Button>
          }
        />
      ))}
    </List>
  );
}

function PolicySheet({
  sheet,
  onClose,
  onSaved,
}: {
  sheet: { key: PolicyKey; value: number };
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const label = POLICIES.find((p) => p.key === sheet.key)?.label ?? 'returnwin';
  const [value, setValue] = useState(String(sheet.value));
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: (v: number) => updatePolicy(sheet.key, v),
    onSuccess: onSaved,
    onError: (e) => setError(t(messageKey(refusalCode(e)))),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const n = Number(value);
    if (!/^\d+$/.test(value.trim()) || n > 365) {
      setError(t('err.number'));
      return;
    }
    setError(undefined);
    save.mutate(n);
  };
  return (
    <BottomSheet
      title={`${t('edit')} · ${t(label)}`}
      sub={sheet.key === 'return_days' ? t('policy.sub') : undefined}
      open
      onClose={onClose}
      busy={save.isPending}
    >
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Field label={t(label)} error={error}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('save')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

function LanguageSheet({
  current,
  onClose,
  onSaved,
}: {
  current: Language;
  onClose: () => void;
  onSaved: (l: Language) => void;
}) {
  const { t } = useTranslation();
  const [choice, setChoice] = useState<Language>(current);
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: () => setBusinessLanguage(choice),
    onSuccess: () => onSaved(choice),
    onError: (e) => setError(t(messageKey(refusalCode(e)))),
  });
  return (
    <BottomSheet title={t('lang.title')} open onClose={onClose} busy={save.isPending}>
      {error && <Notice tone="danger">{error}</Notice>}
      <Chips
        label={t('bizlang')}
        items={languages.map((l) => ({ id: l, label: LANGUAGE_NAMES[l] }))}
        selected={[choice]}
        onToggle={(id) => setChoice(id as Language)}
      />
      <Notice tone="info">{t('lang.sub', { lang: LANGUAGE_NAMES[choice] })}</Notice>
      <SheetActions>
        <Button variant="main" busy={save.isPending} disabled={choice === current} onClick={() => save.mutate()}>
          {t('lang.confirm')}
        </Button>
        <Button onClick={onClose}>{t('cancel')}</Button>
      </SheetActions>
    </BottomSheet>
  );
}

function RoleTabs({ value, onChange }: { value: Role; onChange: (r: Role) => void }) {
  const { t } = useTranslation();
  return (
    <Tabs
      label={t('role')}
      value={value}
      onChange={(id) => onChange(id as Role)}
      items={roles.map((r) => ({ id: r, label: t(`role.${r}`) }))}
    />
  );
}

function NewUserSheet({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('seller');
  const [error, setError] = useState<{ field?: 'name' | 'email'; text: string }>();
  const [created, setCreated] = useState<CreatedUser>();
  const [copied, setCopied] = useState(false);
  const save = useMutation({
    mutationFn: () => createUser({ name: name.trim(), email: email.trim(), role }),
    onSuccess: (u) => {
      setCreated(u);
      onCreated();
    },
    onError: (e) => {
      const code = refusalCode(e);
      setError({
        field: code === 'EMAIL_TAKEN' || code === 'INVALID_EMAIL' ? 'email' : undefined,
        text: t(messageKey(code)),
      } as { field?: 'name' | 'email'; text: string });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError({ field: 'name', text: t('err.required') });
    if (!email.trim() && role !== 'seller') return setError({ field: 'email', text: t('err.required') });
    setError(undefined);
    save.mutate();
  };
  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.temporaryPassword);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  if (created) {
    return (
      <BottomSheet title={t('newuser')} open onClose={onClose}>
        <Notice tone="warning">{t('temppass.once')}</Notice>
        <Field label={t('email')}>{({ id }) => <Input id={id} dir="ltr" value={created.email} readOnly />}</Field>
        <Field label={t('temppass')} hint={t('temppass.hint')}>
          {({ id, describedBy }) => (
            <div className="yv-input-row">
              <Input
                id={id}
                dir="ltr"
                className="yv-code"
                value={created.temporaryPassword}
                readOnly
                aria-describedby={describedBy}
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={() => void copy()}>{copied ? t('copied') : t('copy')}</Button>
            </div>
          )}
        </Field>
        <SheetActions>
          <Button variant="main" onClick={onClose}>
            {t('done')}
          </Button>
        </SheetActions>
      </BottomSheet>
    );
  }
  return (
    <BottomSheet title={t('newuser')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        {error && !error.field && <Notice tone="danger">{error.text}</Notice>}
        <Field label={t('name')} error={error?.field === 'name' ? error.text : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              dir="auto"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('email')} hint={t('internal')} error={error?.field === 'email' ? error.text : undefined}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="email"
              dir="ltr"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('role')}>{() => <RoleTabs value={role} onChange={setRole} />}</Field>
        <SheetActions>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('create.user')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

function EditUserSheet({ user, onClose, onSaved }: { user: UserRow; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const me = useSession();
  const [role, setRole] = useState<Role>(user.role);
  const [error, setError] = useState<string>();
  const onError = (e: unknown) => setError(t(messageKey(refusalCode(e))));
  const saveRole = useMutation({ mutationFn: () => updateUserRole(user.user_id, role), onSuccess: onSaved, onError });
  const toggle = useMutation({
    mutationFn: () => setUserActive(user.user_id, !user.active),
    onSuccess: onSaved,
    onError,
  });
  const busy = saveRole.isPending || toggle.isPending;
  return (
    <BottomSheet title={t('edituser')} sub={<Name>{user.display_name}</Name>} open onClose={onClose} busy={busy}>
      {error && <Notice tone="danger">{error}</Notice>}
      <Field label={t('role')}>{() => <RoleTabs value={role} onChange={setRole} />}</Field>
      {user.user_id !== me.userId &&
        (user.active ? (
          <>
            <Button variant="danger" block busy={toggle.isPending} onClick={() => toggle.mutate()}>
              {t('deactivate')}
            </Button>
            <p className="yv-auth__lead">{t('deact.hint')}</p>
          </>
        ) : (
          <Button block busy={toggle.isPending} onClick={() => toggle.mutate()}>
            {t('reactivate')}
          </Button>
        ))}
      <SheetActions>
        <Button
          variant="main"
          busy={saveRole.isPending}
          disabled={role === user.role}
          onClick={() => saveRole.mutate()}
        >
          {t('save')}
        </Button>
        <Button onClick={onClose}>{t('cancel')}</Button>
      </SheetActions>
    </BottomSheet>
  );
}
